import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatDate, formatTRY, todayISODate } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Zap, Camera, Share2, MessageCircle, Copy, ImagePlus, Trash, CheckCircle, Car, Printer } from "lucide-react";
import CarDiagram, { OPERATIONS } from "@/components/CarDiagram";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";
import { GROUPS } from "./Parts";

const JOB_TYPES = ["Boya", "Kaporta", "Boyasız Onarım", "Pasta Cila", "Şase Düzeltme"];
const STATUSES = ["Beklemede", "Teslim Edildi", "Kısmi Ödeme", "Tahsil Edildi"];

// Grup → İş Türü eşleştirmesi
const GROUP_TO_JOBTYPE = {
  "parca-boyama": "Boya",
  "komple-boya": "Boya",
  "kaporta": "Kaporta",
  "parca-degisim": "Kaporta",
  "sasi-olcum": "Şase Düzeltme",
  "diger": "Boyasız Onarım",
};

const STATUS_COLOR = {
  Beklemede: "bg-amber-50 text-amber-800 border-amber-200",
  "Teslim Edildi": "bg-blue-50 text-blue-800 border-blue-200",
  "Kısmi Ödeme": "bg-violet-50 text-violet-800 border-violet-200",
  "Tahsil Edildi": "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const emptyForm = {
  customer_id: "",
  customer_name: "",
  customer_phone: "",
  plate: "",
  car_model: "",
  job_type: "Boya",
  description: "",
  price: 0,
  vat_rate: 0,
  status: "Beklemede",
  expected_delivery: "",
  date: todayISODate(),
  paid_amount: 0,
  line_items: [], // { id, part_key, part_label, operation, price }
};

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  // Ay filtresi
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const monthOptions = useMemo(() => {
    const opts = [];
    const cur = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(cur.getFullYear(), cur.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const months = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
      opts.push({ value: key, label: `${months[d.getMonth()]} ${d.getFullYear()}` });
    }
    return opts;
  }, []);

  // Cascading picker state
  const [pickerGroup, setPickerGroup] = useState("parca-boyama");
  const [pickerVariant, setPickerVariant] = useState(""); // "Normal" | "Metalik" | "Sedefli" | ""

  const load = async () => {
    const [j, c, p] = await Promise.all([
      api.get("/jobs"),
      api.get("/customers"),
      api.get("/parts"),
    ]);
    setItems(j.data);
    setCustomers(c.data);
    setParts(p.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setPickerGroup("parca-boyama");
    setPickerVariant("");
    setOpen(true);
  };
  const openEdit = (j) => {
    setEditing(j);
    setForm({
      customer_id: j.customer_id || "",
      customer_name: j.customer_name || "",
      customer_phone: j.customer_phone || "",
      plate: j.plate || "",
      car_model: j.car_model || "",
      job_type: j.job_type,
      description: j.description || "",
      price: j.price,
      vat_rate: j.vat_rate || 0,
      status: j.status,
      expected_delivery: j.expected_delivery ? j.expected_delivery.slice(0, 10) : "",
      date: (j.date || todayISODate()).slice(0, 10),
      paid_amount: j.paid_amount || 0,
      line_items: (j.line_items || []).map((li) => ({ ...li })),
    });
    setOpen(true);
  };

  // Diyagramdan işlem seçilince line_items'a satır ekle
  const addLineFromDiagram = (partKey, partLabel, operation) => {
    setForm((prev) => {
      const newItem = {
        id: crypto.randomUUID?.() || String(Math.random()),
        part_key: partKey,
        part_label: partLabel,
        operation,
        price: 0,
      };
      const nextLines = [...(prev.line_items || []), newItem];
      const opSet = new Set(
        (prev.job_type || "").split(",").map((s) => s.trim()).filter(Boolean)
      );
      opSet.add(operation);
      return {
        ...prev,
        line_items: nextLines,
        job_type: Array.from(opSet).join(", "),
      };
    });
    toast.success(`${partLabel} · ${operation} eklendi`);
  };

  const updateLinePrice = (id, price) => {
    setForm((prev) => {
      const nextLines = (prev.line_items || []).map((it) =>
        it.id === id ? { ...it, price: Number(price) || 0 } : it
      );
      const total = nextLines.reduce((s, it) => s + (Number(it.price) || 0), 0);
      return { ...prev, line_items: nextLines, price: total };
    });
  };

  const removeLine = (id) => {
    setForm((prev) => {
      const nextLines = (prev.line_items || []).filter((it) => it.id !== id);
      const total = nextLines.reduce((s, it) => s + (Number(it.price) || 0), 0);
      return { ...prev, line_items: nextLines, price: total };
    });
  };

  // Diyagram için: hangi parça+işlem kombinasyonları seçili
  const diagramSelections = (form.line_items || []).reduce((acc, it) => {
    acc[`${it.part_key}__${it.operation}`] = true;
    return acc;
  }, {});

  // Aktif grup ve alt kategorileri
  const activeGroup = useMemo(() => GROUPS.find((g) => g.key === pickerGroup) || GROUPS[0], [pickerGroup]);
  const availableVariants = useMemo(() => {
    // "Parça Boyama - Normal" → "Normal"
    const vs = activeGroup.categories.map((c) => c.split(" - ")[1]).filter(Boolean);
    return Array.from(new Set(vs));
  }, [activeGroup]);
  const activeCategories = useMemo(() => {
    if (availableVariants.length === 0) return activeGroup.categories;
    if (!pickerVariant) return [activeGroup.categories[0]]; // default ilk varyant
    return activeGroup.categories.filter((c) => c.endsWith(pickerVariant));
  }, [activeGroup, availableVariants, pickerVariant]);

  // ---- Fotoğraf & Paylaşım ----
  const [detailJob, setDetailJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const beforeRef = React.useRef();
  const afterRef = React.useRef();

  const openDetail = (j) => setDetailJob(j);
  const closeDetail = () => setDetailJob(null);

  const refreshDetailJob = async () => {
    if (!detailJob) return;
    const r = await api.get("/jobs");
    const updated = r.data.find((x) => x.id === detailJob.id);
    setItems(r.data);
    if (updated) setDetailJob(updated);
  };

  const uploadPhoto = async (kind, fileList) => {
    if (!detailJob || !fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/jobs/${detailJob.id}/photos?kind=${kind}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${fileList.length} fotoğraf yüklendi`);
      await refreshDetailJob();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    if (!detailJob) return;
    await api.delete(`/jobs/${detailJob.id}/photos/${photoId}`);
    toast.success("Fotoğraf silindi");
    await refreshDetailJob();
  };

  const shareUrl = detailJob
    ? `${window.location.origin}/track/${detailJob.share_token}`
    : "";

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const sendViaWhatsApp = () => {
    if (!detailJob) return;
    const raw = (detailJob.customer_phone || "").replace(/\D/g, "");
    // TR ülke kodu ekle (0 ile başlıyorsa 90 ile değiştir; 90 yoksa +90 ekle)
    let phone = raw;
    if (phone.startsWith("0")) phone = "90" + phone.slice(1);
    else if (!phone.startsWith("90") && phone.length === 10) phone = "90" + phone;
    const msg =
      `Merhaba ${detailJob.customer_name},\n` +
      `${detailJob.plate ? `${detailJob.plate} plakalı ` : ""}aracınızın son durumu: *${detailJob.status}*.\n` +
      `Aracınızı buradan takip edebilirsiniz:\n${shareUrl}\n\n` +
      `Global Car Service`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const regenerateToken = async () => {
    if (!detailJob) return;
    if (!window.confirm("Mevcut paylaşım linki geçersiz olacak ve yeni bir link üretilecek. Devam edilsin mi?")) return;
    await api.post(`/jobs/${detailJob.id}/regenerate-share-token`);
    toast.success("Yeni paylaşım linki oluşturuldu");
    await refreshDetailJob();
  };

  // Kategori filtresine uyan parçalar
  const filteredParts = useMemo(
    () => parts.filter((p) => activeCategories.includes(p.category)),
    [parts, activeCategories]
  );

  // Varsayılan varyant: grup değişince ilk varyantı seç
  useEffect(() => {
    if (availableVariants.length > 0 && !availableVariants.includes(pickerVariant)) {
      setPickerVariant(availableVariants[0]);
    }
    if (availableVariants.length === 0) setPickerVariant("");
  }, [availableVariants, pickerVariant]);

  const addPartToJob = (partId) => {
    const p = parts.find((x) => x.id === partId);
    if (!p) return;
    const currentPrice = Number(form.price) || 0;
    const newPrice = currentPrice + Number(p.price || 0);
    const line = `${p.name} · ${p.category} — ${formatTRY(p.price)}`;
    const desc = form.description ? `${form.description}\n${line}` : line;
    // Add job type to the existing selection (multi-select)
    const newType = GROUP_TO_JOBTYPE[pickerGroup];
    const currentTypes = (form.job_type || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const mergedTypes = newType && !currentTypes.includes(newType)
      ? [...currentTypes, newType]
      : currentTypes;
    setForm({
      ...form,
      price: newPrice,
      description: desc,
      job_type: mergedTypes.join(", "),
    });
    toast.success(`${p.name} eklendi (+${formatTRY(p.price)})`);
  };
  const save = async () => {
    if (!form.customer_name.trim()) {
      toast.error("Müşteri adı gerekli");
      return;
    }
    try {
      const payload = { ...form, price: Number(form.price) || 0, vat_rate: Number(form.vat_rate) || 0, paid_amount: Number(form.paid_amount) || 0 };
      if (editing) {
        await api.put(`/jobs/${editing.id}`, payload);
        toast.success("İş güncellendi");
      } else {
        await api.post("/jobs", payload);
        toast.success("İş eklendi");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };
  const del = async () => {
    await api.delete(`/jobs/${deleteId}`);
    toast.success("Silindi");
    setDeleteId(null);
    load();
  };

  // Ay bazlı filtreleme + geçmiş aylardan devir kayıtları
  const isSettled = (j) => j.status === "Tahsil Edildi";
  const attribMonth = (j) => {
    // Tahsil edildiyse collected_date, teslim edildiyse delivered_date, aksi halde date
    if (isSettled(j) && j.collected_date) return j.collected_date.slice(0, 7);
    if (j.status === "Kısmi Ödeme" && j.collected_date) return j.collected_date.slice(0, 7);
    if (j.status === "Teslim Edildi" && j.delivered_date) return j.delivered_date.slice(0, 7);
    return (j.date || "").slice(0, 7);
  };
  const itemsInMonth = items.filter((j) => {
    const jm = (j.date || "").slice(0, 7);
    if (attribMonth(j) === selectedMonth) return true;
    // Devir: geçmiş ayda açılmış ve hâlâ tahsil edilmemiş
    return jm && jm < selectedMonth && !isSettled(j);
  });

  const gross = (j) => (Number(j.price) || 0) * (1 + (Number(j.vat_rate) || 0));
  const remaining = (j) => Math.max(gross(j) - (Number(j.paid_amount) || 0), 0);

  // Tab filter (Tümü / Bekleyen / Tahsil Edilenler)
  const [tab, setTab] = useState("all");
  const tabbed = itemsInMonth.filter((j) => {
    if (tab === "pending") return !isSettled(j);
    if (tab === "delivered") return isSettled(j);
    return true;
  });

  const filtered = tabbed.filter((j) => {
    const t = q.toLowerCase();
    return (
      !q ||
      j.customer_name?.toLowerCase().includes(t) ||
      j.plate?.toLowerCase().includes(t) ||
      j.job_type?.toLowerCase().includes(t)
    );
  });

  // Ay bazlı özet
  const monthTotals = {
    total: itemsInMonth.reduce((s, j) => s + gross(j), 0),
    collected: itemsInMonth.reduce((s, j) => {
      if (isSettled(j)) return s + gross(j);
      if (j.status === "Kısmi Ödeme") return s + (Number(j.paid_amount) || 0);
      return s;
    }, 0),
    pending: itemsInMonth.filter((j) => !isSettled(j)).reduce((s, j) => s + remaining(j), 0),
  };
  const monthLabel = monthOptions.find((o) => o.value === selectedMonth)?.label;

  return (
    <div data-testid="jobs-page" className="space-y-6">
      {/* HEADER — month filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">İş Kayıtları</p>
          <p className="text-sm text-slate-700">
            Ay: <span data-testid="jobs-active-month-label" className="font-semibold text-slate-900">{monthLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="jobs-month-select" className="w-52 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Tutar</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="jobs-total-amount">
            {formatTRY(monthTotals.total)}
          </p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
          <p className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">Tahsil Edilen</p>
          <p className="font-display text-2xl font-bold text-emerald-700 mt-2 font-mono-num" data-testid="jobs-delivered-amount">
            {formatTRY(monthTotals.collected)}
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <p className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">Bekleyen</p>
          <p className="font-display text-2xl font-bold text-amber-700 mt-2 font-mono-num" data-testid="jobs-pending-amount-page">
            {formatTRY(monthTotals.pending)}
          </p>
        </div>
      </div>

      {/* Tabs + Yeni buton */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { key: "all", label: "Tümü" },
              { key: "pending", label: "Bekleyen" },
              { key: "delivered", label: "Tahsil Edilenler" },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`jobs-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                  tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="jobs-search"
              placeholder="Müşteri, plaka, tür…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
        </div>
        <Button data-testid="add-job-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni İş Kaydı
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Tarih", "Müşteri / Plaka", "Tür", "Açıklama", "Tutar", "Durum", ""]} />
        <tbody>
          {filtered.length === 0 && (
            <EmptyRow colSpan={7} testId="jobs-empty" message="Henüz iş kaydı yok. Yeni iş ekleyin." />
          )}
          {filtered.map((j) => {
            const jobMonth = (j.date || "").slice(0, 7);
            const collectedMonth = (j.collected_date || "").slice(0, 7);
            const openedInPastMonth = jobMonth && jobMonth < selectedMonth;
            const isCarriedPending =
              openedInPastMonth && !isSettled(j);
            const isCarriedDelivered =
              openedInPastMonth &&
              isSettled(j) &&
              collectedMonth === selectedMonth;
            const rowTint = isCarriedPending
              ? "bg-rose-50/60 hover:bg-rose-100/60"
              : isCarriedDelivered
              ? "bg-emerald-50/60 hover:bg-emerald-100/60"
              : "hover:bg-slate-50/60";
            return (
            <tr
              key={j.id}
              className={`border-b border-slate-100 ${rowTint}`}
              data-testid={`job-row-${j.id}`}
            >
              <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                {formatDate(j.date)}
                {isCarriedPending && (
                  <div
                    data-testid={`job-carried-badge-${j.id}`}
                    className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-rose-300 bg-rose-100 text-rose-800"
                    title="Geçmiş aylardan devir"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                    DEVİR
                  </div>
                )}
                {isCarriedDelivered && (
                  <div
                    data-testid={`job-carried-delivered-badge-${j.id}`}
                    className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-emerald-300 bg-emerald-100 text-emerald-800"
                    title={`Devir kayıt · Bu ay tahsil edildi (${formatDate(j.date)} açılış)`}
                  >
                    <CheckCircle className="w-2.5 h-2.5" />
                    DEVİR · TAHSİL
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-900">{j.customer_name}</div>
                {j.plate && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 bg-slate-100 rounded font-mono-num text-[10px] font-bold text-slate-700">
                    {j.plate}
                  </span>
                )}
                {j.car_model && (
                  <div className="text-[11px] text-slate-500 mt-0.5">{j.car_model}</div>
                )}
              </td>
              <td className="px-4 py-3 text-sm">
                <div className="flex flex-wrap gap-1">
                  {(j.job_type || "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((t) => (
                      <span
                        key={t}
                        className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium"
                      >
                        {t}
                      </span>
                    ))}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{j.description || "-"}</td>
              <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">
                {formatTRY(j.price * (1 + (j.vat_rate || 0)))}
                {(j.vat_rate || 0) > 0 && (
                  <div className="text-[10px] text-slate-500 font-normal">
                    KDV dahil ({(j.vat_rate * 100).toFixed(0)}%)
                  </div>
                )}
                {j.status === "Kısmi Ödeme" && (
                  <div className="text-[10px] text-violet-700 font-medium mt-0.5">
                    Ödenen: {formatTRY(j.paid_amount || 0)} · Kalan: {formatTRY(remaining(j))}
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[j.status]}`}>
                  {j.status}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    data-testid={`detail-job-${j.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => openDetail(j)}
                    className="h-8 w-8 hover:bg-blue-50"
                    title="Fotoğraf & Müşteriye Bildir"
                  >
                    <Camera className="w-4 h-4 text-blue-600" />
                  </Button>
                  <Button
                    data-testid={`print-job-${j.id}`}
                    variant="ghost"
                    size="icon"
                    onClick={() => window.open(`/print/job/${j.id}`, "_blank")}
                    className="h-8 w-8 hover:bg-slate-100"
                    title="Yazdır / PDF"
                  >
                    <Printer className="w-4 h-4 text-slate-700" />
                  </Button>
                  <Button data-testid={`edit-job-${j.id}`} variant="ghost" size="icon" onClick={() => openEdit(j)} className="h-8 w-8">
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-job-${j.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(j.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </TableWrap>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="job-dialog" className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "İşi Düzenle" : "Yeni İş Kaydı"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* MÜŞTERİ */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 text-[11px] uppercase tracking-widest font-semibold">
                  Müşteri
                </Label>
                {customers.length > 0 && (
                  <Select
                    value=""
                    onValueChange={(id) => {
                      const c = customers.find((x) => x.id === id);
                      if (c) {
                        setForm({
                          ...form,
                          customer_id: c.id,
                          customer_name: c.name || "",
                          customer_phone: c.phone || "",
                          plate: c.plate || "",
                          car_model: c.car_model || "",
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="job-customer-existing" className="h-7 w-44 text-xs bg-white">
                      <SelectValue placeholder="Kayıtlı müşteriden…" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.plate ? `· ${c.plate}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Input
                  data-testid="job-customer-name"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })}
                  placeholder="Ad Soyad *"
                  className="bg-white h-9 text-sm"
                />
                <Input
                  data-testid="job-customer-phone"
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value, customer_id: "" })}
                  placeholder="Telefon"
                  className="bg-white h-9 text-sm"
                />
                <Input
                  data-testid="job-customer-plate"
                  value={form.plate}
                  onChange={(e) => setForm({ ...form, plate: e.target.value, customer_id: "" })}
                  placeholder="Plaka"
                  className="bg-white h-9 text-sm font-mono-num"
                />
                <Input
                  data-testid="job-customer-model"
                  value={form.car_model}
                  onChange={(e) => setForm({ ...form, car_model: e.target.value, customer_id: "" })}
                  placeholder="Araç Modeli"
                  className="bg-white h-9 text-sm"
                />
              </div>
            </div>

            {/* ARABA DİYAGRAMI + İŞ KALEMLERİ */}
            <details className="bg-slate-50 border border-slate-200 rounded-md" open>
              <summary
                data-testid="car-diagram-toggle"
                className="cursor-pointer flex items-center gap-2 px-3 py-2 select-none hover:bg-slate-100 rounded-md"
              >
                <Car className="w-4 h-4 text-slate-600" />
                <span className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold">
                  Araç Şeması · Parçadan İşlem Ekle
                </span>
                {(form.line_items || []).length > 0 && (
                  <span className="ml-auto text-[11px] font-semibold text-slate-500">
                    {form.line_items.length} kalem
                  </span>
                )}
              </summary>
              <div className="p-3 pt-2 border-t border-slate-200 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <CarDiagram
                    selections={diagramSelections}
                    onSelectPart={addLineFromDiagram}
                  />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                    Seçilen İş Kalemleri
                  </p>
                  {(form.line_items || []).length === 0 ? (
                    <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 rounded p-4 text-center">
                      Henüz parça seçilmedi. Şemadan parçaya tıklayın.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {form.line_items.map((it) => {
                        const op = OPERATIONS.find((o) => o.key === it.operation);
                        return (
                          <div
                            key={it.id}
                            data-testid={`line-item-${it.id}`}
                            className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2"
                          >
                            <span
                              className={`shrink-0 w-2 h-2 rounded-full ${op?.bg || "bg-slate-300"}`}
                              title={op?.label}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-900 truncate">{it.part_label}</p>
                              <p className="text-[10px] text-slate-500">{it.operation}</p>
                            </div>
                            <Input
                              data-testid={`line-price-${it.id}`}
                              type="number"
                              step="0.01"
                              value={it.price}
                              onChange={(e) => updateLinePrice(it.id, e.target.value)}
                              className="w-24 h-8 text-xs font-mono-num text-right"
                              placeholder="₺"
                            />
                            <button
                              type="button"
                              onClick={() => removeLine(it.id)}
                              data-testid={`remove-line-${it.id}`}
                              className="shrink-0 p-1 hover:bg-rose-50 rounded"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                            </button>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2">
                        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Ara Toplam</span>
                        <span className="font-mono-num font-bold text-slate-900" data-testid="line-items-total">
                          {formatTRY(form.line_items.reduce((s, it) => s + (Number(it.price) || 0), 0))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* İŞ DETAYLARI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">İş Türü <span className="text-slate-400 normal-case">(çoklu seçim)</span></Label>
                <div
                  data-testid="job-type-toggles"
                  className="mt-1 inline-flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1 border border-slate-200"
                >
                  {JOB_TYPES.map((t) => {
                    const selected = (form.job_type || "")
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean);
                    const active = selected.includes(t);
                    const toggle = () => {
                      let next;
                      if (active) next = selected.filter((x) => x !== t);
                      else next = [...selected, t];
                      setForm({ ...form, job_type: next.join(", ") });
                    };
                    return (
                      <button
                        key={t}
                        type="button"
                        data-testid={`job-type-${t}`}
                        onClick={toggle}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          active
                            ? "bg-slate-900 text-white shadow-sm"
                            : "bg-transparent text-slate-600 hover:text-slate-900 hover:bg-white/60"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="job-status-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* KISMİ ÖDEME TUTARI */}
            {form.status === "Kısmi Ödeme" && (
              <div className="bg-violet-50 border border-violet-200 rounded-md p-3">
                <Label className="text-[11px] uppercase tracking-widest text-violet-800 font-semibold">
                  Tahsil Edilen Tutar (₺)
                </Label>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    data-testid="job-paid-amount-input"
                    type="number"
                    step="0.01"
                    value={form.paid_amount}
                    onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
                    className="font-mono-num h-9 text-sm bg-white max-w-[200px]"
                    placeholder="₺"
                  />
                  <div className="text-xs text-violet-800">
                    Kalan: <span className="font-mono-num font-bold">
                      {formatTRY(Math.max(
                        (Number(form.price) || 0) * (1 + (Number(form.vat_rate) || 0)) - (Number(form.paid_amount) || 0),
                        0
                      ))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* TUTAR */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tutar (₺)</Label>
                <Input
                  data-testid="job-price-input"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="font-mono-num h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">KDV</Label>
                <Select
                  value={String(form.vat_rate)}
                  onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}
                >
                  <SelectTrigger data-testid="job-vat-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">KDV Yok</SelectItem>
                    <SelectItem value="0.2">%20 KDV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                {Number(form.price) > 0 && Number(form.vat_rate) > 0 ? (
                  <div className="w-full bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-xs">
                    <div className="font-semibold text-blue-900 font-mono-num">
                      Toplam: {formatTRY(Number(form.price) * (1 + Number(form.vat_rate)))}
                    </div>
                    <div className="text-[10px] text-blue-700">
                      +KDV {formatTRY(Number(form.price) * Number(form.vat_rate))}
                    </div>
                  </div>
                ) : (
                  <div className="w-full bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs text-slate-500">
                    KDV eklenmedi
                  </div>
                )}
              </div>
            </div>

            {/* FİYAT LİSTESİNDEN HIZLI EKLE — açılır kapanır */}
            <details className="bg-amber-50/50 border border-amber-200 rounded-md">
              <summary
                data-testid="job-picker-toggle"
                className="cursor-pointer flex items-center gap-2 px-3 py-2 select-none hover:bg-amber-100/50 rounded-md"
              >
                <Zap className="w-4 h-4 text-amber-600" />
                <span className="text-[11px] uppercase tracking-widest text-slate-700 font-semibold">
                  Fiyat listesinden hızlı ekle
                </span>
                <span className="ml-auto text-[11px] text-slate-500">(opsiyonel)</span>
              </summary>
              <div className="p-3 pt-2 space-y-2 border-t border-amber-200">
                <div className="flex gap-1 flex-wrap">
                  {GROUPS.filter((g) => g.key !== "diger").map((g) => {
                    const Icon = g.icon;
                    const active = pickerGroup === g.key;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        data-testid={`picker-group-${g.key}`}
                        onClick={() => setPickerGroup(g.key)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                          active
                            ? `bg-gradient-to-br ${g.color} text-white border-transparent shadow-sm`
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <Icon className="w-3 h-3" />
                        {g.label}
                      </button>
                    );
                  })}
                </div>
                {availableVariants.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Renk:</span>
                    {availableVariants.map((v) => {
                      const active = pickerVariant === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          data-testid={`picker-variant-${v}`}
                          onClick={() => setPickerVariant(v)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border transition-colors ${
                            active
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                          }`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                )}
                <Select value="" onValueChange={addPartToJob}>
                  <SelectTrigger data-testid="job-part-picker" className="bg-white h-8 text-xs">
                    <SelectValue
                      placeholder={
                        filteredParts.length === 0
                          ? "Bu kategoride işlem yok"
                          : `${activeGroup.label}${pickerVariant ? ` · ${pickerVariant}` : ""} → seç (${filteredParts.length})`
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {filteredParts.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-500">Bu kategori boş.</div>
                    )}
                    {filteredParts.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`job-part-option-${p.id}`}>
                        {p.name} · <span className="font-mono-num">{formatTRY(p.price)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </details>

            {/* AÇIKLAMA */}
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Açıklama</Label>
              <Textarea
                data-testid="job-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Örn: Ön tampon boya + sağ çamurluk düzeltme"
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-job-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FOTOĞRAF & PAYLAŞIM DİYALOĞU */}
      <Dialog open={!!detailJob} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent data-testid="job-detail-dialog" className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              {detailJob?.customer_name}
              {detailJob?.plate && <span className="font-mono-num text-sm text-slate-500">· {detailJob.plate}</span>}
            </DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-6 py-2">
              {/* Müşteri Paylaşım */}
              <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-emerald-700" />
                  <p className="text-sm font-semibold text-emerald-900">Müşteri Paylaşım Linki</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Input
                    data-testid="share-url-input"
                    value={shareUrl}
                    readOnly
                    className="font-mono-num text-xs bg-white flex-1 min-w-[240px]"
                  />
                  <Button
                    data-testid="copy-share-btn"
                    onClick={copyShareUrl}
                    variant="outline"
                    className="border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Kopyala
                  </Button>
                  <Button
                    data-testid="whatsapp-share-btn"
                    onClick={sendViaWhatsApp}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-1.5" />
                    WhatsApp&apos;tan Gönder
                  </Button>
                </div>
                <div className="flex items-center justify-between text-[11px] text-emerald-800/80">
                  <span>
                    {detailJob.customer_phone
                      ? `Telefon: ${detailJob.customer_phone}`
                      : "Müşteri telefonu boş — WhatsApp seçim ekranı açılır."}
                  </span>
                  <button
                    type="button"
                    onClick={regenerateToken}
                    data-testid="regenerate-token-btn"
                    className="underline hover:text-emerald-900"
                  >
                    Yeni link üret
                  </button>
                </div>
              </section>

              {/* Öncesi */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-900">Öncesi Fotoğrafları</p>
                  <div>
                    <input
                      ref={beforeRef}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      data-testid="upload-before-input"
                      onChange={(e) => uploadPhoto("before", e.target.files)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => beforeRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-before-btn"
                    >
                      <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> Ekle
                    </Button>
                  </div>
                </div>
                <PhotoGrid
                  photos={(detailJob.photos || []).filter((p) => p.kind === "before")}
                  onDelete={deletePhoto}
                  jobId={detailJob.id}
                  emptyLabel="Henüz öncesi fotoğrafı yok."
                />
              </section>

              {/* Sonrası */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-900">Sonrası Fotoğrafları</p>
                  <div>
                    <input
                      ref={afterRef}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      data-testid="upload-after-input"
                      onChange={(e) => uploadPhoto("after", e.target.files)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => afterRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-after-btn"
                    >
                      <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> Ekle
                    </Button>
                  </div>
                </div>
                <PhotoGrid
                  photos={(detailJob.photos || []).filter((p) => p.kind === "after")}
                  onDelete={deletePhoto}
                  jobId={detailJob.id}
                  emptyLabel="Henüz sonrası fotoğrafı yok."
                />
              </section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDetail}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İş kaydı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-job" onClick={del} className="bg-rose-600 hover:bg-rose-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Alt bileşen: fotoğraf grid'i (öncesi/sonrası)
function PhotoGrid({ photos, onDelete, jobId, emptyLabel }) {
  if (!photos || photos.length === 0) {
    return (
      <div className="border-2 border-dashed border-slate-200 rounded-md p-6 text-center text-xs text-slate-500">
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      {photos.map((p) => (
        <div key={p.id} className="group relative aspect-square border border-slate-200 rounded-md overflow-hidden bg-slate-100">
          <img
            src={`${API}/jobs/${jobId}/photos/${p.id}`}
            alt={p.filename || "Fotoğraf"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <button
            type="button"
            data-testid={`delete-photo-${p.id}`}
            onClick={() => onDelete(p.id)}
            className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            title="Sil"
          >
            <Trash className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
